// v49.12 — NotificationsBell.
// Topbar bell with unread count badge. Click opens a dropdown showing
// the latest 8 unread notifications + a link to the full /notifications
// page. Subscribes to placement_notifications inserts so the count
// updates live.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LABOURCORE_API } from "@/lib/api";
import { tradeLabel, stageLabel } from "@/features/requirements/labels";
import { routeFor } from "@/features/mail/notificationRoute";
import type { NotificationKind, PlacementNotification } from "@/lib/api/types";

interface NotificationRow extends PlacementNotification {
  placement?: {
    /* v50.213 — id needed so routeFor can deep-link the bell click into
       /deals/{id}. listForUser already selects it via the
       placement_id(id, ...) projection on the API side. */
    id?: string;
    operative?: { full_name?: string; trade?: string } | null;
  } | null;
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen]               = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent]           = useState<NotificationRow[] | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    const [count, list] = await Promise.all([
      LABOURCORE_API.placement_notifications.unreadCount(),
      LABOURCORE_API.placement_notifications.listForUser({ onlyUnread: true, limit: 8 }),
    ]);
    setUnreadCount(count);
    setRecent((list as NotificationRow[]) || []);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /* Realtime: new notifications bump the badge instantly. */
  useEffect(() => {
    const sub = LABOURCORE_API.placement_notifications.subscribe(() => { void refresh(); });
    return () => { try { sub.unsubscribe?.(); } catch { /* ignore */ } };
  }, [refresh]);

  /* Close on outside click. */
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  /* v50.213 — brief §6: clicking a notification routes to its area.
     Mark-read fires in parallel; the navigate happens immediately so
     the user doesn't wait on the DB write. */
  const onItemClick = (n: NotificationRow) => {
    if (!n.read_at) {
      /* Fire-and-forget so the navigate isn't blocked. */
      void LABOURCORE_API.placement_notifications.markRead(n.id).then(refresh);
    }
    setOpen(false);
    navigate(routeFor(n));
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen(v => !v)}
        className="relative w-9 h-9 rounded-lc-md flex items-center justify-center text-lc-mute hover:bg-lc-bg-2 hover:text-lc-ink transition-colors"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-lc-red text-white text-[9px] font-extrabold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-80 max-h-96 overflow-y-auto rounded-lc-md border border-lc-line bg-white shadow-2xl z-30">
          <div className="px-3 py-2.5 border-b border-lc-line flex items-center gap-2">
            <span className="text-xs font-extrabold uppercase tracking-wider text-lc-mute flex-1">Notifications</span>
            {unreadCount > 0 && (
              <span className="text-[10px] font-bold text-lc-blue-dark">{unreadCount} unread</span>
            )}
          </div>

          {recent == null && (
            <div className="px-3 py-4 text-center text-xs text-lc-faint">Loading…</div>
          )}
          {recent != null && recent.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-lc-mute italic">No unread notifications.</div>
          )}
          {recent && recent.length > 0 && (
            <div className="flex flex-col">
              {recent.map(n => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className="text-left px-3 py-2.5 hover:bg-lc-bg-2/60 border-b border-lc-line last:border-b-0 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className={`inline-block w-1.5 h-1.5 mt-1.5 rounded-full ${dotTone(n.kind)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold text-lc-ink truncate">{shortHeadline(n)}</div>
                      <div className="text-[10px] text-lc-mute mt-0.5">
                        {n.placement?.operative?.full_name || "Placement"}
                        {n.placement?.operative?.trade && ` · ${tradeLabel(n.placement.operative.trade)}`}
                      </div>
                    </div>
                    {n.created_at && (
                      <span className="text-[10px] text-lc-faint whitespace-nowrap">
                        {new Date(n.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-lc-line">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block px-3 py-2.5 text-center text-xs font-extrabold text-lc-blue hover:bg-lc-blue-light/40 transition-colors"
            >
              View all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function dotTone(kind: NotificationKind): string {
  switch (kind) {
    case "message":                  return "bg-lc-blue";
    case "stage_change":             return "bg-lc-amber";
    case "log_submitted":            return "bg-lc-faint";
    case "log_confirmed":            return "bg-lc-green";
    case "rating":                   return "bg-lc-green";
    case "rating_due":               return "bg-lc-amber";
    case "contract_offer_sent":      return "bg-lc-blue";
    case "contract_offer_responded": return "bg-lc-amber";
    case "compliance_requested":     return "bg-lc-amber";
    case "compliance_fulfilled":     return "bg-lc-green";
    case "compliance_expiring":      return "bg-lc-red";
    /* v50.213 — extend coverage to all kinds added since v49.12. */
    case "dispute_filed":                  return "bg-lc-red";
    case "dispute_updated":                return "bg-lc-amber";
    case "milestone_evidence_submitted":   return "bg-lc-blue";
    case "milestone_marked_met":           return "bg-lc-green";
    case "milestone_deemed_met":           return "bg-lc-amber";
    case "invoice_sent":                   return "bg-lc-blue";
    case "invoice_paid":                   return "bg-lc-green";
    case "invoice_aging":                  return "bg-lc-red";
    case "bid_opportunity_matched":        return "bg-lc-blue";
    case "tender_amendment_published":     return "bg-lc-amber";
    case "tender_clarification_raised":    return "bg-lc-amber";
    case "tender_clarification_answered":  return "bg-lc-blue";
    case "tender_invitation_received":     return "bg-lc-blue";
    case "tender_invitation_reminder":     return "bg-lc-amber";
    case "bid_pack_submitted":             return "bg-lc-blue";
    case "bid_outcome_shortlisted":        return "bg-lc-blue";
    case "bid_outcome_won":                return "bg-lc-green";
    case "bid_outcome_lost":               return "bg-lc-red";
    case "futures_booking_received":       return "bg-lc-amber";
    case "futures_booking_confirmed":      return "bg-lc-green";
    case "futures_booking_declined":       return "bg-lc-red";
    case "partner_offer_received":         return "bg-lc-amber";
    case "partner_offer_accepted":         return "bg-lc-green";
    case "partner_offer_declined":         return "bg-lc-red";
    case "partner_offer_withdrawn":        return "bg-lc-faint";
    case "proposal_received":              return "bg-lc-blue";
    case "proposal_aging":                 return "bg-lc-amber";
    case "log_unconfirmed_aging":          return "bg-lc-amber";
    default:                               return "bg-lc-faint";
  }
}

function shortHeadline(n: NotificationRow): string {
  const payload = n.payload || {};
  const opp = payload.opportunity_title ? `"${String(payload.opportunity_title)}"` : "";
  const proj = payload.project_name ? `"${String(payload.project_name)}"` : "";
  switch (n.kind) {
    case "message":                  return String(payload.preview || "New message");
    case "stage_change":             return `Stage → ${payload.to ? stageLabel(payload.to as never) : "updated"}`;
    case "log_submitted":            return `Daily log submitted${payload.hours_worked != null ? ` (${payload.hours_worked}h)` : ""}`;
    case "log_confirmed":            return "Daily log confirmed";
    case "rating":                   return "You were rated";
    case "rating_due":               return `Rate your placement${payload.counterparty_name ? ` with ${payload.counterparty_name}` : ""}`;
    case "contract_offer_sent":      return String(payload.role) === "coordinator"
                                            ? `Offer sent to ${payload.partner_name || "partner"}`
                                            : `New contract offer from ${payload.coordinator_name || "coordinator"}`;
    case "contract_offer_responded": return `Offer ${payload.status || "responded"}`;
    case "compliance_requested":     return `${payload.cert_label || payload.cert_kind || "Cert"} requested`;
    case "compliance_fulfilled":     return `${payload.cert_label || payload.cert_kind || "Cert"} ${payload.status || "fulfilled"}`;
    case "compliance_expiring":      return `${payload.cert_label || payload.cert_kind || "Cert"} expiring`;
    /* v50.213 — extend coverage to all kinds. */
    case "dispute_filed":                  return `Dispute filed${payload.title ? ` — ${payload.title}` : ""}`;
    case "dispute_updated":                return `Dispute ${payload.to || "updated"}`;
    case "milestone_evidence_submitted":   return `Evidence submitted${payload.description ? ` — ${payload.description}` : ""}`;
    case "milestone_marked_met":           return `Milestone met${payload.description ? ` — ${payload.description}` : ""}`;
    case "milestone_deemed_met":           return `Milestone deemed met${payload.description ? ` — ${payload.description}` : ""}`;
    case "invoice_sent":                   return `${payload.invoice_number || "Invoice"} sent`;
    case "invoice_paid":                   return `${payload.invoice_number || "Invoice"} paid`;
    case "invoice_aging":                  return `${payload.invoice_number || "Invoice"} unpaid`;
    case "bid_opportunity_matched":        return `Tender match ${opp}`.trim();
    case "tender_amendment_published":     return `Amendment on ${opp || "a tender"}`;
    case "tender_clarification_raised":    return `Q&A raised on ${opp || "your tender"}`;
    case "tender_clarification_answered":  return `Q&A answered on ${opp || "a tender"}`;
    case "tender_invitation_received":     return `Invited to bid on ${opp || "a tender"}`;
    case "tender_invitation_reminder":     return `Invitation reminder ${opp}`.trim();
    case "bid_pack_submitted":             return `Bid received ${opp}`.trim();
    case "bid_outcome_shortlisted":        return `Shortlisted on ${opp || "a tender"}`;
    case "bid_outcome_won":                return `You won ${opp || "a tender"}`;
    case "bid_outcome_lost":               return `Bid not selected on ${opp || "a tender"}`;
    case "futures_booking_received":       return `Booking request on ${proj || "a project"}`;
    case "futures_booking_confirmed":      return `Booking confirmed on ${proj || "a project"}`;
    case "futures_booking_declined":       return `Booking declined on ${proj || "a project"}`;
    case "partner_offer_received":         return `Crew offer on ${proj || "a project"}`;
    case "partner_offer_accepted":         return `Crew offer accepted on ${proj || "a project"}`;
    case "partner_offer_declined":         return `Crew offer declined on ${proj || "a project"}`;
    case "partner_offer_withdrawn":        return `Crew offer withdrawn on ${proj || "a project"}`;
    case "proposal_received":              return `New proposal${payload.operative_name ? ` — ${payload.operative_name}` : ""}`;
    case "proposal_aging":                 return "Proposal pending";
    case "log_unconfirmed_aging":          return "Log unconfirmed";
    default:                               return "Activity";
  }
}
